import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "portfolio",
  title: "Portfolio overview and holdings",
  navLabel: "Portfolio",
  description:
    "Review account value, holdings, deployed strategies, and Risk Controls for one Portfolio Account or an account group.",
  section: "portfolio-trading",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["Mavori portfolio", "review portfolio holdings", "portfolio risk controls"],
  related: ["accounts", "deployments", "trading", "activity"],
  blocks: [
    {
      type: "lead",
      text: "Portfolio brings connected, Manual, and Paper Accounts into one place. Select an account or an account group to review value, holdings, deployed strategies, and Risk Controls at the scope you intend.",
    },

    { type: "h2", text: "Choose the right scope" },
    {
      type: "p",
      text: "The **Accounts** view groups Portfolio Accounts under Live and Paper. Select **View total** to review an account-type aggregate, or open one account for its own holdings and controls. On smaller screens, the same flow becomes a list-first account browser with a Back action from account detail.",
    },
    {
      type: "p",
      text: "Keep the selected scope in mind when reading totals. An aggregate combines the accounts shown in that group; an account view is the ownership boundary used by trading, Risk Controls, and Strategy Deployments.",
    },

    { type: "h2", text: "Read value and performance" },
    {
      type: "p",
      text: "The valuation panel shows account value over the selected range together with available cash, invested value, contributions, return, and drawdown measures. Its source and as-of information explain whether the value came from a broker, recorded positions, Paper Account activity, or a mixture.",
    },
    {
      type: "callout",
      variant: "info",
      title: "Check scope and freshness",
      text: "Before making a decision, confirm the selected account, the valuation's as-of time, and any partial-coverage message. An unavailable price remains unavailable rather than being silently treated as current.",
    },

    { type: "h2", text: "Inspect holdings" },
    {
      type: "p",
      text: "The Positions section can show all holdings or focus on equities or options. Rows carry the position size and the available cost, value, allocation, and profit-and-loss context. Open an eligible Manual Account position to review or update its recorded details; broker-reported positions remain tied to their connection.",
    },
    {
      type: "p",
      text: "Account views also show Strategy Deployments funded by that account. Open the linked Strategy when you need its version, backtest, status, performance, or fill history. See [Strategy deployments](/docs/deployments) for the full lifecycle.",
    },

    { type: "h2", text: "Set and review Risk Controls" },
    {
      type: "p",
      text: "Risk Controls live on each account's Portfolio view. They define the trading limits and review requirements applied to that account. Aggregate views summarize account risk and link back to the account that owns each control. Review the enforcement label on every control so you know whether it blocks an order, requires review, or monitors account state.",
    },
    {
      type: "p",
      text: "Use **Trade Logs** and **Journal** at the top of Portfolio to move from current state to historical activity. [Trade Logs and Journal](/docs/activity) explains how orders, fills, and decision records connect.",
    },
  ],
};
